interface LinkInfo {
  href: string;
  title: string;
  text: string;
}

export const extractTitle = async (file: File): Promise<string> => {
  try {
    const text = await file.text();
    const titleMatch = text.match(/<title>([^<]*)<\/title>/i);
    return titleMatch ? titleMatch[1].trim() : file.name;
  } catch (error) {
    return file.name;
  }
};

export const extractLinks = async (
  file: File,
  allFiles: File[]
): Promise<LinkInfo[]> => {
  try {
    const text = await file.text();
    const links: LinkInfo[] = [];
    const foundHrefs = new Set<string>(); // To avoid duplicates

    // Pattern 1: link-to-page figures (your existing pattern)
    const linkToPagePattern =
      /<figure[^>]*class="link-to-page"[^>]*>[\s\S]*?<a[^>]*href="([^"]+)"[^>]*>([\s\S]*?)<\/a>[\s\S]*?<\/figure>/gi;

    // Pattern 2: Regular anchor tags with .html hrefs
    const regularLinkPattern =
      /<a[^>]*href="([^"]+\.html[^"]*)"[^>]*>([\s\S]*?)<\/a>/gi;

    // Extract link-to-page style links
    let match;
    while ((match = linkToPagePattern.exec(text)) !== null) {
      const href = match[1];
      const fullText = match[2];

      if (!href.includes(".html") || foundHrefs.has(href)) {
        continue;
      }

      foundHrefs.add(href);

      const textOnly = fullText
        .replace(/<[^>]*>/g, "")
        .replace(/\s+/g, " ")
        .trim();

      links.push({
        href: href,
        title: "",
        text: textOnly,
      });
    }

    // Extract regular anchor links
    while ((match = regularLinkPattern.exec(text)) !== null) {
      const href = match[1];
      const fullText = match[2];

      // Skip if we already found this link or if it's not a local HTML file
      if (foundHrefs.has(href) || href.startsWith("http")) {
        continue;
      }

      foundHrefs.add(href);

      const textOnly = fullText
        .replace(/<[^>]*>/g, "")
        .replace(/\s+/g, " ")
        .trim();

      // Skip empty text links
      if (!textOnly) {
        continue;
      }

      links.push({
        href: href,
        title: "",
        text: textOnly,
      });
    }

    console.log(`Found ${links.length} links in ${file.name}`);

    return links;
  } catch (error) {
    console.error("Error extracting links:", error);
    return [];
  }
};

export const findFileByHref = (
  href: string,
  allFiles: File[]
): File | undefined => {
  if (!href) return undefined;

  // Clean the href - remove any URL encoding and anchors
  const cleanHref = href.split("#")[0];

  // Try to find exact match first
  let found = allFiles.find((file) => file.name === cleanHref);

  if (!found) {
    // Try to match just the filename part
    const hrefFilename = cleanHref.split("/").pop() || cleanHref;
    found = allFiles.find((file) => {
      const fileName = file.name.split("/").pop() || file.name;
      return fileName === hrefFilename;
    });
  }

  if (!found) {
    // Try URL decoded version
    try {
      const decodedHref = decodeURIComponent(cleanHref);
      const decodedFilename = decodedHref.split("/").pop() || decodedHref;

      found = allFiles.find((file) => {
        const fileName = file.name.split("/").pop() || file.name;
        return fileName === decodedFilename || file.name === decodedHref;
      });
    } catch (e) {
      // Ignore decode errors
    }
  }

  if (found) {
    console.log(`Matched ${href} to ${found.name}`);
  } else {
    console.log(`No match found for ${href}`);
  }

  return found;
};
